import type { User } from 'firebase/auth';

import { clearScribeConversationState } from '../actions/conversation';
import { logout } from '../store/slices/auth';
import { toggleSideBar } from '../store/slices/uiState';
import { deleteLocalAndSessionStorageAndCookies } from './cache';
import { firebaseFunctionsEndpoint } from './http';
import * as segment from './segment';

export enum HEARING_PROFILES {
  DEAF = 'deaf',
  HARD_OF_HEARING = 'hard-of-hearing',
  HEARING = 'hearing',
}

export enum VOICING_PROFILES {
  NOT_VOICING = 'not voicing',
  VOICING = 'voicing',
}

export const HEARING_PROFILES_OPTIONS = {
  // $FlowFixMe
  0: 'hearingProfile.deaf',
  // $FlowFixMe
  1: 'hearingProfile.hardOfHearing',
  // $FlowFixMe
  2: 'hearingProfile.hearing',
};

export const isUserNameValid = (userName) => {
  return userName.trim().length >= 2 && userName.trim().length < 50;
};

export const getUserColor = (username) => {
  if (!username) {
    return 'grey';
  }
  let hash = 0;
  username.split('').forEach((c) => {
    // eslint-disable-next-line
    hash = c.charCodeAt(0) + ((hash << 5) - hash);
  });
  const h = hash % 360;
  // eslint-disable-next-line
  return 'hsl(' + h + ', ' + 50 + '%, ' + 50 + '%)';
};

export const isDevMode = (features, backends) => {
  return !!features?.['dev-mode'] || localStorage.getItem('avaBackendUrl') === backends.production.url;
};

const unregisterServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) {
        registration.unregister();
      }
    });
  }
};

export const logoutAndClean = async (redirectPath = '/') => {
  segment.track('Signed Out');
  segment.reset();
  deleteLocalAndSessionStorageAndCookies();
  unregisterServiceWorker();

  window.store.dispatch(clearScribeConversationState());
  window.store.dispatch(toggleSideBar(false));
  await logout(window.store.dispatch, true);

  // Firebase can end up in a state where without deleting these databases,
  // no-one can ever log in anymore. Since we're about to redirect (which
  // refreshes the page), we need to delete them here.
  indexedDB.deleteDatabase('firebase-installations-database');
  indexedDB.deleteDatabase('firebaseLocalStorageDb');

  if (window.electronIPC) {
    window.electronIPC.sendSetIsLoggedIn(false);
    window.electronIPC.sendCCMode('expand');
  }

  //@ts-expect-error
  window.location = redirectPath;
};

/**
 * Handles SAML logout if user is authenticated via SAML; otherwise, does nothing.
 * Opens a popup window for IdP logout and monitors it until closed.
 * Caller is responsible for performing app logout after this function completes.
 */
export const handleSamlLogout = async (firebaseUser: User | null): Promise<void> => {
  if (!firebaseUser) {
    return;
  }

  try {
    const idTokenResult = await firebaseUser.getIdTokenResult();
    const { samlProviderId, samlNameId } = idTokenResult.claims;

    if (!samlProviderId && !samlNameId) {
      return;
    }

    const idToken = await firebaseUser.getIdToken();

    const response = await fetch(`${firebaseFunctionsEndpoint}/auth/saml/logout`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });

    if (!response.ok) {
      // Fallback to regular logout
      return;
    }

    const data = await response.json();
    const { logoutUrl } = data;

    if (!logoutUrl) {
      // No logout URL from IdP, proceed with regular logout
      return;
    }

    // Open popup window for IdP logout
    const popupWidth = 600;
    const popupHeight = 700;
    const left = window.screenX + (window.outerWidth - popupWidth) / 2;
    const top = window.screenY + (window.outerHeight - popupHeight) / 2;

    const popup = window.open(
      logoutUrl,
      'saml_logout',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      // Fallback to regular logout
      return;
    }

    // Monitor popup window and resolve when closed
    return new Promise<void>((resolve) => {
      // eslint-disable-next-line prefer-const
      let timeoutId: NodeJS.Timeout;

      const checkPopupClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopupClosed);
          clearTimeout(timeoutId);
          // Popup closed, resolve so caller can perform app logout
          resolve();
        }
      }, 500);

      // Timeout after 60 seconds to prevent infinite interval
      timeoutId = setTimeout(() => {
        clearInterval(checkPopupClosed);
        // Force close popup if still open
        if (!popup.closed) {
          popup.close();
        }
        // Resolve anyway so logout can proceed
        resolve();
      }, 60000);
    });
  } catch (error) {
    // Fallback to regular logout if there's an error
    return;
  }
};

if (window.electronIPC) {
  window.electronIPC.onSignOutAndShowSignIn(async () => {
    await logoutAndClean('/web/login');
  });
}

export const buildUrl = (baseUrl: string, params: Record<string, any>) => {
  const searchParams = new URLSearchParams();

  for (const key in params) {
    const value = params[key];
    if (value !== '') {
      searchParams.append(key, value);
    }
  }

  return `${baseUrl}?${searchParams.toString()}`;
};
